import React, { RefObject, useState, useCallback } from 'react';

import { useDetectClickOutside } from '@eika/hooks';

interface ReturnType {
    show: boolean;
    setShow: (value: boolean) => void;
    onKeyDownSelect: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
    onKeyDownSelectItem: (event: React.KeyboardEvent<HTMLLIElement | HTMLButtonElement>) => void;
    onKeyDownSearchInput: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    closeSelect: () => void;
}

const focusableElementsSelector =
    'button:not([tabindex="-1"]):not([disabled]), input:not([tabindex="-1"]):not([disabled])';

const getFocusable = (el: HTMLUListElement) => {
    if (!el) {
        return [];
    }
    return Array.from(el.querySelectorAll<HTMLElement>(focusableElementsSelector) ?? []);
};

const useSelect = (
    selectList: RefObject<HTMLUListElement>,
    selectContainer: RefObject<HTMLDivElement>,
    select: RefObject<HTMLButtonElement | HTMLInputElement>,
): ReturnType => {
    const [show, setShow] = useState(false);

    useDetectClickOutside(selectContainer, () => {
        setShow(false);
    });

    const selectFirstElementInList = useCallback((focusableItems: HTMLElement[]) => {
        if (focusableItems.length > 0) {
            focusableItems[0].focus();
        }
    }, []);

    const closeSelect = useCallback(() => {
        select.current?.focus();
        setShow(false);
    }, [select, setShow]);
    const onKeyDownSelectItem = useCallback(
        (e: React.KeyboardEvent<HTMLLIElement | HTMLButtonElement>) => {
            const focusable = selectList?.current ? getFocusable(selectList.current) : [];
            const focusedItem = focusable.find((el) => el === document.activeElement);

            const numberOfFocusableItems = focusable.length;

            const focusedItemIndex = focusedItem ? focusable.indexOf(focusedItem) : 0;
            const isOnFristElement = focusedItemIndex === 0;
            const isOnLastElement = focusedItemIndex === numberOfFocusableItems - 1;
            const lastElementIndex = numberOfFocusableItems - 1;
            switch (e.key) {
                case 'Down':
                case 'ArrowDown':
                    e.stopPropagation();
                    e.preventDefault();

                    if (isOnLastElement) {
                        focusable[0].focus();
                        return;
                    }
                    focusable[focusedItemIndex + 1].focus();
                    break;

                case 'Up':
                case 'ArrowUp':
                    e.preventDefault();
                    if (isOnFristElement) {
                        focusable[lastElementIndex].focus();
                    } else {
                        focusable[focusedItemIndex - 1].focus();
                    }
                    break;

                case 'Esc':
                case 'Escape':
                    closeSelect();
                    break;

                case 'Tab':
                case 'Enter':
                    e.preventDefault();
                    closeSelect();
                    break;
                default:
                    break;
            }
        },
        [closeSelect, selectList],
    );

    const onKeyDownSelect = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            const focusable = selectList?.current ? getFocusable(selectList.current) : [];
            switch (e.key) {
                case 'Down':
                case 'ArrowDown':
                    e.stopPropagation();
                    e.preventDefault();
                    selectFirstElementInList(focusable);
                    setShow(true);
                    break;
                case 'Tab':
                    closeSelect();
                    break;
                case 'ArrowUp':
                case 'Up':
                    e.preventDefault();
                    closeSelect();
                    break;
                default:
                    break;
            }
        },
        [selectList, closeSelect, selectFirstElementInList],
    );

    const onKeyDownSearchInput = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const focusable = selectList?.current ? getFocusable(selectList.current) : [];
            switch (e.key) {
                case 'Down':
                case 'ArrowDown':
                    e.stopPropagation();
                    selectFirstElementInList(focusable);
                    setShow(true);
                    break;

                case 'Esc':
                case 'Escape':
                    closeSelect();
                    break;

                case 'Enter':
                    e.preventDefault();
                    break;

                case 'Tab':
                    closeSelect();
                    break;

                default:
                    break;
            }
        },
        [setShow, selectList, closeSelect, selectFirstElementInList],
    );

    return { show, setShow, onKeyDownSelect, onKeyDownSelectItem, onKeyDownSearchInput, closeSelect };
};

export default useSelect;
